import _toConsumableArray from '@babel/runtime/helpers/toConsumableArray';
import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import { jsx, jsxs } from 'react/jsx-runtime';
import React, { useState } from 'react';
import { useGridApiContext } from '@mui/x-data-grid-premium';
import { useConfigContextLoose, Flex, Button, Skeleton, CounterChip } from '@cognite/cogs-core';
import { FilterFilledIcon, FilterIcon } from '@cognite/cogs-icons';
import { FilterForm } from '../page-toolbar/elements.js';
import { SelectPanel } from '../select-panel/select-panel.js';

var MultiSelectFilter = function MultiSelectFilter(_ref) {
  var _ref$filter = _ref.filter,
    title = _ref$filter.title,
    options = _ref$filter.options,
    isSearchable = _ref$filter.isSearchable,
    isLoading = _ref$filter.isLoading,
    onSearch = _ref$filter.onSearch,
    resultCount = _ref$filter.resultCount,
    hideResultCount = _ref$filter.hideResultCount,
    showSelectedOptionOnTop = _ref$filter.showSelectedOptionOnTop,
    showSelectAll = _ref$filter.showSelectAll,
    portalTarget = _ref.portalTarget,
    filterTrigger = _ref.filterTrigger,
    columnFilterState = _ref.columnFilterState,
    onColumnFilterChange = _ref.onColumnFilterChange,
    multiSelectFilterLocaleText = _ref.multiSelectFilterLocaleText;
  var _useState = useState(''),
    _useState2 = _slicedToArray(_useState, 2),
    searchText = _useState2[0],
    setSearchText = _useState2[1];
  var _useState3 = useState(columnFilterState.value),
    _useState4 = _slicedToArray(_useState3, 1),
    selectedOptions = _useState4[0];
  var areAllOptionsSelected = React.useMemo(function () {
    return options.every(function (option) {
      return columnFilterState.value.includes(option.value);
    });
  }, [columnFilterState.value, options]);
  var _useConfigContextLoos = useConfigContextLoose(),
    getOverlayContainer = _useConfigContextLoos.getOverlayContainer;
  var isSelectAllIndeterminate = columnFilterState.value.length > 0 && !areAllOptionsSelected;
  var filteredOptions = React.useMemo(function () {
    var nextFilteredOptions;
    if (onSearch) {
      nextFilteredOptions = options;
    } else {
      nextFilteredOptions = options.filter(function (option) {
        return option.label.toLowerCase().includes(searchText.toLowerCase()) || showSelectedOptionOnTop && columnFilterState.value.includes(option.value);
      });
    }
    return showSelectedOptionOnTop ? [].concat(_toConsumableArray(nextFilteredOptions.filter(function (option) {
      return selectedOptions.includes(option.value);
    })), _toConsumableArray(nextFilteredOptions.filter(function (option) {
      return !selectedOptions.includes(option.value);
    }))) : nextFilteredOptions;
  }, [onSearch, showSelectedOptionOnTop, options, searchText, columnFilterState.value, selectedOptions]);
  var toggleSelectAll = function toggleSelectAll() {
    onColumnFilterChange === null || onColumnFilterChange === undefined ? undefined : onColumnFilterChange(Object.assign(Object.assign({}, columnFilterState), {
      value: areAllOptionsSelected ? [] : filteredOptions.map(function (option) {
        return option.value;
      })
    }));
  };
  var filteredOptionsCount = React.useMemo(function () {
    if (onSearch) {
      return resultCount;
    }
    return filteredOptions.length;
  }, [onSearch, filteredOptions.length, resultCount]);
  var handleCheckboxChange = function handleCheckboxChange(checked, option) {
    if (!checked) {
      var updatedState = columnFilterState.value.filter(function (val) {
        return val !== option.value;
      });
      onColumnFilterChange === null || onColumnFilterChange === undefined ? undefined : onColumnFilterChange(Object.assign(Object.assign({}, columnFilterState), {
        value: updatedState
      }));
    } else {
      onColumnFilterChange === null || onColumnFilterChange === undefined ? undefined : onColumnFilterChange(Object.assign(Object.assign({}, columnFilterState), {
        value: [].concat(_toConsumableArray(columnFilterState.value), [option.value])
      }));
    }
  };
  /**
   * Handle keyboard navigation for select all and options
   *
   * @param event keyboard keydown event
   * @param index Select all checkbox is represented by index -1
   */
  var handleKeyDown = function handleKeyDown(event, index) {
    var _a, _b;
    if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
      event.preventDefault();
      // We need to add 1 to the total elements and index if the select all checkbox is shown
      // This will increase the range of nextIndex by 1 if the select all checkbox is shown
      var additionalElements = showSelectAll ? 1 : 0;
      var totalElements = filteredOptions.length + additionalElements;
      var adjustedIndex = index + additionalElements;
      // We need to subtract 1 from the index if the select all checkbox is shown
      // This will decrease the range of nextIndex by 1 if the select all checkbox is shown
      var nextIndex = (event.key === 'ArrowUp' ? (adjustedIndex - 1 + totalElements) % totalElements : (adjustedIndex + 1) % totalElements) - additionalElements;
      if (nextIndex === -1) {
        (_a = document.getElementById("checkbox-select-all")) === null || _a === undefined ? undefined : _a.focus();
      } else {
        var nextItemValue = filteredOptions[nextIndex].value;
        (_b = document.getElementById("checkbox-".concat(nextItemValue))) === null || _b === undefined ? undefined : _b.focus();
      }
    }
  };
  var showResultCount = isSearchable && !hideResultCount && !isLoading;
  var showHeader = title || isSearchable || showResultCount;
  return jsxs(SelectPanel, {
    onClick: function onClick(e) {
      e.stopPropagation();
    },
    appendTo: portalTarget !== null && portalTarget !== undefined ? portalTarget : getOverlayContainer === null || getOverlayContainer === undefined ? undefined : getOverlayContainer(),
    hideOnOutsideClick: true,
    children: [jsx(SelectPanel.Trigger, {
      children: filterTrigger || jsx(Flex, {
        alignItems: "center",
        justifyContent: "center",
        children: jsx(Button, {
          type: "ghost",
          "aria-label": "Filter",
          size: "small",
          icon: columnFilterState.value.length > 0 ? jsx(FilterFilledIcon, {}) : jsx(FilterIcon, {})
        })
      })
    }), showHeader && jsxs(SelectPanel.Header, {
      title: title,
      children: [!isLoading && showSelectAll && filteredOptions.length > 0 && jsx(SelectPanel.SelectAll, {
        id: "checkbox-select-all",
        label: (multiSelectFilterLocaleText === null || multiSelectFilterLocaleText === undefined ? undefined : multiSelectFilterLocaleText.filterOptionsSelectAll) || 'Select all',
        onChange: function onChange(_ev) {
          toggleSelectAll();
        },
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore Checkbox doesn't expose this prop but does pass it down as expected
        onKeyDown: function onKeyDown(e) {
          handleKeyDown(e, -1);
        },
        indeterminate: isSelectAllIndeterminate,
        checked: areAllOptionsSelected || isSelectAllIndeterminate
      }), isSearchable && jsx(SelectPanel.SearchBar, {
        type: "search",
        placeholder: (multiSelectFilterLocaleText === null || multiSelectFilterLocaleText === undefined ? undefined : multiSelectFilterLocaleText.filterSearchPlaceholder) || 'Search',
        value: searchText,
        onChange: function onChange(ev) {
          setSearchText(ev.target.value);
          onSearch === null || onSearch === undefined ? undefined : onSearch(ev.target.value);
        }
      }), showResultCount && jsxs("div", {
        children: [(multiSelectFilterLocaleText === null || multiSelectFilterLocaleText === undefined ? undefined : multiSelectFilterLocaleText.filterSearchResult) || 'Results', ' ', filteredOptionsCount]
      })]
    }), jsxs(SelectPanel.Body, {
      children: [isLoading && jsx(Flex, {
        direction: "column",
        justifyContent: "center",
        style: {
          padding: 4
        },
        gap: 8,
        children: [0, 1, 2].map(function (key) {
          return jsx(Skeleton.Rectangle, {}, key);
        })
      }), filteredOptions.length === 0 && jsx("div", {
        className: "select-filter-results",
        children: "No Options found"
      }), jsx(SelectPanel.Section, {
        children: !isLoading && filteredOptions.map(function (option, index) {
          return jsx(SelectPanel.Item, {
            id: "checkbox-".concat(option.value),
            label: option.label,
            variant: "checkbox",
            trailingContent: (option.count || option.count === 0) && jsx(CounterChip, {
              counter: option.count
            }),
            onActiveChange: function onActiveChange(next) {
              handleCheckboxChange(next, option);
            },
            checked: columnFilterState.value.includes(option.value)
          });
        })
      })]
    })]
  });
};
var DatagridToolbarColumnHeaderFilter = function DatagridToolbarColumnHeaderFilter() {
  var apiRef = useGridApiContext();
  var columns = apiRef.current.getAllColumns();
  var filterModel = apiRef.current.state.filter.filterModel;
  return jsx(FilterForm, {
    columns: columns.map(function (col) {
      return {
        label: col.headerName,
        value: col.field,
        type: col.type || 'string'
      };
    }),
    filterModel: filterModel,
    onFilterModelChange: apiRef.current.setFilterModel
  });
};

export { DatagridToolbarColumnHeaderFilter, MultiSelectFilter };
